import { ComponentBase } from '@abstracts/component-base';
import { Component, Injector, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Customer } from '@models/customer';
import { CustomerPermission } from '@models/customerPermission';
import { SubscriptionTypePermission } from '@models/subscriptionTypePermission';
import { CustomersService } from '@services/entity/customers.service';
import { SubscriptionsService } from '@services/entity/subscriptions.service';
import { UsersService } from '@services/entity/users.service';
import { FormService } from '@services/form/form.service';
import { ToastService } from '@services/toast/toast.service';
import { plainToClass } from 'class-transformer';

@Component({
  selector: 'app-customer-permissions',
  templateUrl: './customer-permissions.component.html',
  styleUrls: ['./customer-permissions.component.scss']
})
export class CustomerPermissionsComponent extends ComponentBase implements OnInit {

  public className = 'CustomerPermissionsComponent';

  private customerId: number;
  public subscriptionTypeId: number;

  public permissionMap: Map<string, CustomerPermission | SubscriptionTypePermission>;

  public formCreated: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private customersService: CustomersService,
    private subscriptionsService: SubscriptionsService,
    private toastService: ToastService,
    private usersService: UsersService,
    public formService: FormService,
    injector: Injector) {
      super(injector);
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.componentSettings.footerVisible = false;
    this.customerId = this.activatedRoute.parent.snapshot.params.key;
    this.initializeData();
  }

  async initializeData() {
    await this.loadSubscriptionType();
    this.loadPermissions();
  }

  loadPermissions() {
    if (this.subscriptionTypeId == 4) {
      this.permissionMap = new Map<string, CustomerPermission>();
      this.customersService.getCustomerPermissions(this.customerId).subscribe(result => {
        this.setupPermissionMap(result);
        this.createForm();
      }, error => { });
    }
    else {
      this.permissionMap = new Map<string, SubscriptionTypePermission>();
      this.subscriptionsService.getSubscriptionTypePermissions(this.subscriptionTypeId).subscribe(result => {
        this.setupPermissionMap(result);
        this.createForm();
      }, error => { });
    }

  }

  async loadSubscriptionType() {
    this.subscriptionTypeId = await this.customersService.getSubscriptionTypeAsync(this.customerId);
  }

  setupPermissionMap(permissions: CustomerPermission[] | SubscriptionTypePermission[]) {
    permissions.forEach(permission => {
      this.permissionMap.set(permission.permission.name, permission);
    })
  }

  changeSubscriptionType(typeId: number) {
    this.customersService.patchSubscriptionType(this.customerId, typeId).subscribe(async result => {
      this.initializeData();
      this.toastService.success('Abo-Modell wurde erfolgreich geändert.');
    }, error => {
      this.toastService.error('Abo-Modell konnte nicht geändert werden.');
    })
  }

  createForm() {
    this.formService.form = new UntypedFormGroup({
      subscriptionTypeId: new UntypedFormControl(this.subscriptionTypeId),

      navDashboard: new UntypedFormControl(this.permissionMap.get("navDashboard").value),
      navChargingstations: new UntypedFormControl(this.permissionMap.get("navChargingstations").value),
      navEvaluation: new UntypedFormControl(this.permissionMap.get("navEvaluation").value),
      navManage: new UntypedFormControl(this.permissionMap.get("navManage").value),
      dashboardChargingstations: new UntypedFormControl(this.permissionMap.get("dashboardChargingstations").value),
      dashboardPowerchart: new UntypedFormControl(this.permissionMap.get("dashboardPowerchart").value),
      dashboardLatestTransactions: new UntypedFormControl(this.permissionMap.get("dashboardLatestTransactions").value),
      dashboardProfile: new UntypedFormControl(this.permissionMap.get("dashboardProfile").value),
      dashboardPowerchartDay: new UntypedFormControl(this.permissionMap.get("dashboardPowerchartDay").value),
      dashboardPowerchartMonth: new UntypedFormControl(this.permissionMap.get("dashboardPowerchartMonth").value),
      dashboardPowerchartYear: new UntypedFormControl(this.permissionMap.get("dashboardPowerchartYear").value),
      chargingstationsGroups: new UntypedFormControl(this.permissionMap.get("chargingstationsGroups").value),
      chargingstationsCreate: new UntypedFormControl(this.permissionMap.get("chargingstationsCreate").value),
      chargingstationDetailsAuth: new UntypedFormControl(this.permissionMap.get("chargingstationDetailsAuth").value),
      chargingstationDetailsTransactions: new UntypedFormControl(this.permissionMap.get("chargingstationDetailsTransactions").value),
      chargingstationFreecharge: new UntypedFormControl(this.permissionMap.get("chargingstationFreecharge").value),
      chargingstationRemoteStart: new UntypedFormControl(this.permissionMap.get("chargingstationRemoteStart").value),
      chargingstationGroupsCreate: new UntypedFormControl(this.permissionMap.get("chargingstationGroupsCreate").value),
      evaluationTransactions: new UntypedFormControl(this.permissionMap.get("evaluationTransactions").value),
      evaluationDeliveryNotes: new UntypedFormControl(this.permissionMap.get("evaluationDeliveryNotes").value),
      chargingStatisticsWeek: new UntypedFormControl(this.permissionMap.get("chargingStatisticsWeek").value),
      chargingStatisticsMonth: new UntypedFormControl(this.permissionMap.get("chargingStatisticsMonth").value),
      chargingStatisticsYear: new UntypedFormControl(this.permissionMap.get("chargingStatisticsYear").value),
      chargingStatisticsCustom: new UntypedFormControl(this.permissionMap.get("chargingStatisticsCustom").value),
      chargingStatisticsFilter: new UntypedFormControl(this.permissionMap.get("chargingStatisticsFilter").value),
      chargingStatisticsCheckbox: new UntypedFormControl(this.permissionMap.get("chargingStatisticsCheckbox").value),
      chargingStatisticsDateFilter: new UntypedFormControl(this.permissionMap.get("chargingStatisticsDateFilter").value),
      evaluationDeliveryNotesSend: new UntypedFormControl(this.permissionMap.get("evaluationDeliveryNotesSend").value),
      manageUsersCreate: new UntypedFormControl(this.permissionMap.get("manageUsersCreate").value),
      manageUsersCreateCount: new UntypedFormControl(this.permissionMap.get("manageUsersCreateCount").value),
      manageRfidScan: new UntypedFormControl(this.permissionMap.get("manageRfidScan").value),
      createDeliveryNotes: new UntypedFormControl(this.permissionMap.get("createDeliveryNotes").value),
    });

    if (this.subscriptionTypeId != 4) {
      setTimeout(() => this.formService.form.disable());
      setTimeout(() => this.formService.form.get("subscriptionTypeId").enable());
    }
    else {
      setTimeout(() => this.formService.form.enable());
    }

    this.formCreated = true;
  }

  onSubmit(formValue) {
    for (var [key, value] of Object.entries(formValue)) {

      var numberValue;

      if (typeof value == "boolean") {
        numberValue = Number(value);
      }
      else {
        numberValue = value;
      }

      var customerPermission = this.permissionMap.get(key);
      if (customerPermission) {
        customerPermission.value = numberValue;

        this.permissionMap.set(key, customerPermission);
      }
    }

    var customerPermissions = Array.from(this.permissionMap.values());

    this.customersService.updateCustomerPermissions(this.customerId, customerPermissions as CustomerPermission[]).subscribe(result => {
      this.toastService.success('Änderungen erfolgreich gespeichert.');
      this.loadPermissions();
    }, error => {
      this.toastService.error('Ein Fehler ist beim speichern aufgetreten.');
    })
  }
}
